import axios from 'axios';
import debounce from 'lodash.debounce';

import customMapControls from './custom_map_controls'
import DrawingManager from './drawing_manager'
import mapConverters from './map_converters'
import MapDataFetcher from './map_data_fetcher'
import mapModals from './map_modals'
import {reasonableNumberShown} from './map_listing_checker'
import {markerManager} from './marker_manager'
import MyLocation from './my_location'
import nosyNeighbor, { NOSY_NEIGHBOR_ZOOM_LEVEL } from './nosy_neighbor'
import OverlayManager from './overlay_manager'
import {searchResults} from './../search_results/search_results'
import {getState, setState} from './../search_results/state'
import {MapPositionStorage} from './../search_results/map_position_storage'
import { iteratee } from 'lodash'
import GlobalOverlayManager from './../global_overlay_manager'
import ShapeEditor from './shape_editor'
import mapEvents from './map_events'
import {
  createDrawnShapeAnnotation,
  removeDrawnShapeAnnotation,
  updateDrawnShapeLabel,
  updateDrawnShapeColor
} from './../search/annotation_helper';
import { filterChangesCoordinatedByMapsController } from './map_controller_helpers';

const COMPLEX_SHAPE_USER_ERROR_MESSAGE = 'We cannot load this search because the shapes contained in the filter are too complex.';
const SHAPE_COORDINATE_ERROR_MESSAGE = 'search total coordinates must be below';
const MAP_CLUSTER_TIMEOUT_MESSAGE_NARROW = 'Please edit your search filters.';
const MAP_CLUSTER_TIMEOUT_MESSAGE_GENERIC = 'Unable to update the map at this time.';

// After fitMapToCurrentListingResults updates bounds without firing mapBounds events,
// ignore bounds_changed cluster refetches until the map settles (longer than the 500ms
// debounced boundsChangedHandler).
const SUPPRESS_BOUNDS_CHANGED_REFETCH_MS = 600;

function isClustersRequestTimeoutError(error) {
  return !!(error && error.response && error.response.status === 408);
}

// **********************************************
//
//  Options:
//
//    mlsDefaultBounds: (array of two points) Required. The default boundaries for the map as specified
//      by the mls from SystemInfo.
//
//    and more...
//
// **********************************************


export function FlexmlsMap(element, options) {
  var self = this;

  // Turn on debugging for different things
  this.debug = false;
  this.debugBounds = false;

  this.element = element;

  this.loadingMessage = options.loadingMessage;

  this.translations = $.extend({}, options.translations);
  this.currencySymbol = options.currencySymbol;

  this.drawingCompleteCallback = options.drawingCompleteCallback || function(){};

  let mapPositionStorage = new MapPositionStorage(mapSupport.mapScopeKey, this);
  mapPositionStorage.listenForMapChanges();

  this._frozen = false;
  this._pendingBoundsClusterRefresh = false;

  markerManager.init({map: this});

  this.googleMapSettings = {
    disableDefaultUI: true,
    gestureHandling: 'greedy', // All touch gestures and scroll events pan or zoom the map.
    minZoom: 5 // don't let the user zoom out too far
  };

  if(options.mlsDefaultBounds) {
    this.mlsDefaultBounds = mapConverters.getGoogleBoundsFromPoints(options.mlsDefaultBounds);
  }

  // show price pins instead of dots if there are less than this many visible listings
  this.pricePinCutoff = 30;

  this.parcelOutlines = [];

  if ($('#confirmDeleteDrawingModal').length === 0) {
    $('body').append(mapModals.confirmDeleteDrawing(this.translations));
  }
  if ($('#drawnShapeManagementMenu').length === 0) {
    $('body').append(mapModals.drawnShapeManagementMenu(this.translations));
  }

  this.mapDataFetcher = new MapDataFetcher({
    url: mapSupport.clusterDataRetrieverUrl,
    fromList: mapSupport.isListDriven
  });
}

FlexmlsMap.prototype = {
  constructor: FlexmlsMap,

  init: function() {
    this._startLoadingMessage();
    this._createMap();

    this.bindMapEvents();

    this.overlayManager = new OverlayManager(this.googleMap);
    GlobalOverlayManager.setMap(this.googleMap)

    const shapeProperties = mapConverters.defaultShapeProperties;
    const drawingProperties = mapConverters.defaultDrawingProperties;
    this.shapeEditor = new ShapeEditor(this.googleMap, {
      polygonOptions: { clickable: false, ...shapeProperties },
      rectangleOptions: { clickable: false, ...shapeProperties },
      circleOptions: { clickable: false, ...shapeProperties },
      ghostLineOptions: {
        strokeColor: shapeProperties.strokeColor,
        strokeOpacity: drawingProperties.strokeOpacity,
        strokeWeight: drawingProperties.strokeWeight,
        clickable: false
      },
      onComplete: (shape) => {
        if (shape && shape.setOptions) {
          shape.setOptions({ clickable: false, ...shapeProperties });
        }
        if (shape instanceof google.maps.Circle) {
          markerManager.addRadiusMarker(shape);
        }
        this.drawingManager.add(shape);
        window.dispatchEvent(mapEvents.drawingComplete(shape));
        setTimeout(() => {
          this.drawingManager.setExternalDrawingInProgress(false);
          this.drawingManager.restoreDrawingsClickability();
        }, 0);
      },
      onCancel: (info) => {
        if (info && info.shape instanceof google.maps.Circle) {
          markerManager.removeShapeMarker(info.shape);
        }
        window.dispatchEvent(mapEvents.drawingComplete());
        setTimeout(() => {
          this.drawingManager.setExternalDrawingInProgress(false);
          this.drawingManager.restoreDrawingsClickability();
        }, 0);
      },
      onError: () => {
        window.dispatchEvent(mapEvents.drawingError());
        setTimeout(() => {
          this.drawingManager.setExternalDrawingInProgress(false);
          this.drawingManager.restoreDrawingsClickability();
        }, 0);
      },
      onChange: (shape) => {
        if (shape && shape.getCenter && shape.getRadius && markerManager && markerManager.addRadiusMarker) {
          markerManager.addRadiusMarker(shape);
        }
      }
    });
    this.drawingManager = new DrawingManager(this.googleMap, markerManager, this.shapeEditor);

    customMapControls.init({
      map: this.googleMap,
      drawingManager: this.drawingManager,
      overlayManager: this.overlayManager
    });

    nosyNeighbor.init(this, this.googleMap);
    this.bindMapControls();
    this.getLocationAndAddMarker();

    this.refreshMapShapes();

    this.drawingCompleteHandler = this.drawingComplete.bind(this);
    this.drawingErrorHandler = this.drawingError.bind(this);
    this.filterChangeHandler = this.handleFilterChanged.bind(this);
    this.drawnShapeEditCompleteHandler = this.drawnShapeEditComplete.bind(this);

    window.addEventListener('map:drawingComplete', this.drawingCompleteHandler);
    window.addEventListener('map:drawingError', this.drawingErrorHandler);
    window.addEventListener('stateChange:filter', this.filterChangeHandler);
    window.addEventListener('map:drawnShapeEditComplete', this.drawnShapeEditCompleteHandler);

    setTimeout(function() {
      GlobalOverlayManager.loadSelectedMlsOverlays()
      GlobalOverlayManager.loadSelectedMyMapOverlays()
    },1000)

  },

  onMapUnload: function() {
    window.removeEventListener('map:drawingComplete', this.drawingCompleteHandler);
    window.removeEventListener('map:drawingError', this.drawingErrorHandler);
    window.removeEventListener('stateChange:mapBounds', this.handleMapBoundsStateChangeHandler );
    window.removeEventListener('stateChange:filter', this.filterChangeHandler);

    // Clean up map event listeners
    if (this.boundsListener) {
      google.maps.event.removeListener(this.boundsListener);
    }
    if (this.zoomListener) {
      google.maps.event.removeListener(this.zoomListener);
    }
    this.unBindMapControls()
  },

  handleFilterChanged: function () {
    // Turbo map pages delegate filter refetch + refit to map--maps#filterChangedCallback.
    if (filterChangesCoordinatedByMapsController()) {
      return;
    }
    if(!this._frozen) {
      this.updateMap();
    }
  },

  _handleMapBoundsStateChange: function() {
    if(!this._frozen){
      this.updateMap();
    } else {
      this._pendingBoundsClusterRefresh = true;
    }
  },

  _createMap: function() {
    var self = this;

    this.handleMapBoundsStateChangeHandler = this._handleMapBoundsStateChange.bind(this)
    this.googleMap = new google.maps.Map(this.element, this.googleMapSettings);
    // Try to restore the map from a previous session.
    const previousSessionBounds = getState('mapBounds');
    if( previousSessionBounds !== null ){
      this._log('restoring previous map position');
      window.addEventListener('stateChange:mapBounds', this.handleMapBoundsStateChangeHandler );
      this.moveMapToBoundary(previousSessionBounds);

    } else {

      let filter = getState('filter');

      if(mapSupport.isListDriven){
        this._log('The map is list driven. Setting up map using the MLS Default Bounds.');
        window.addEventListener('stateChange:mapBounds', this.handleMapBoundsStateChangeHandler );
        this.moveMapToMlsDefaultBounds();
      } else if( filter.locations().length || filter.shapes().length ){
        // If the filter includes locations or shapes shapes, position the map around those shapes.
        this._log('setting up map around locations or shapes');
        this._setUpMapAroundListingData(filter.toString());
      } else {
        // There's no previous session, and there are no shapes in the filter,
        // so use the MLS default bounds.
        this._log('setting up map using the MLS Default Bounds');

        this.moveMapToMlsDefaultBounds();

        const filter = getState('filter').toString();

        // The map will be positioned to include the mls default bounds, but the
        // actual map bounds will be larger than the mls default bounds, so we
        // need to get the the bounds from the map.
        this.getBounds().then((bounds) => {

          this._getListingData(filter, bounds).done(() => {

            if (reasonableNumberShown(this.listingData, bounds)) {
              this.drawMarkers();
              setState('mapBounds', bounds);
              window.addEventListener('stateChange:mapBounds', this.handleMapBoundsStateChangeHandler );
            } else {
              this._log( 'Not enough listings are shown. The MLS default bounds will be abandoned and ths listing data bounds will be used instead.' );
              this._setUpMapAroundListingData(filter);
            }

          });
        });

      }
    }
  },

  // Position the map for the given filter: try shape bounds, then listing boundary,
  // then MLS default. Reusable by any caller that needs to position the map for a
  // filter (e.g. initial load, loading a saved search).
  _positionMapForFilter: function(filter) {
    this._getListingData(filter).done(() => {
      const shapeBounds = this._getBoundsFromFilterShapes();
      if (shapeBounds) {
        window.addEventListener('stateChange:mapBounds', this.handleMapBoundsStateChangeHandler );
        this.moveMapToBoundary(shapeBounds).then((bounds) => {
          this.drawMarkers();
          setState('mapBounds', bounds);
        });
        return;
      }
      this.moveMapToListingDataBoundary().then((bounds) => {
        this.drawMarkers();
        // The map is already in the right spot and all the listings have been
        // correctly plotted on the map. The state needs to be set before the
        // listener is set up so that setting the state doesn't trigger a reload.
        setState('mapBounds', bounds);
        window.addEventListener('stateChange:mapBounds', this.handleMapBoundsStateChangeHandler );
      }).catch((error) => {
        // No listing boundary; fall back to MLS default. Listener is set before
        // moving the map so the move will then trigger the necessary reload.
        window.addEventListener('stateChange:mapBounds', this.handleMapBoundsStateChangeHandler );
        this.moveMapToMlsDefaultBounds();
      });
    });
  },

  // Position the map around the listing data (with fallbacks). Logic lives in _positionMapForFilter.
  _setUpMapAroundListingData: function(filter) {
    this._positionMapForFilter(filter);
  },

  showDrawingModeHeader: function() {
    if($('#drawingModeHeader').length === 0){
      this.insertDrawingModeHeader();
    }
    $('#mapHeader').css('opacity', 0);
    $('#drawingModeHeader').text('Draw to Select Area').show();
  },

  hideDrawingModeHeader: function() {
    $('#mapHeader').css('opacity', 1);
    $('#drawingModeHeader').hide();
  },

  insertDrawingModeHeader: function() {
    var headerHtml = $('<div>', {
      class: 'c-header t-type-base-strong drawing-mode-header',
      id: 'drawingModeHeader',
      text: 'Draw to Select Area'
    });

    $('#mapContainer').append(headerHtml);
  },

  refreshMapShapes: function() {
    // We do not refresh the shapes when frozen.  We are probably adding/deleting
    // them or moving the map if we are frozen.  No need to redraw them at that point.
    // This will protect against callers calling this method in a frozen state.
    if (this._frozen) return;

    // clear all the drawings from the map and redraw shapes from the filter
    this.drawingManager.deleteAll();
    markerManager.deleteRadiusMarkers();
    let filter = getState('filter');
    let annotations = getState('annotations');

    if(filter.shapes().length){
      var self = this;
      filter.shapes().forEach(function(element){
        let annotation = self.getAnnotationByCondition(annotations, element.condition);
        let shape = mapConverters.getGoogleShapeFromFilterShape(element);
        if (shape instanceof google.maps.Circle) {
          markerManager.addRadiusMarker(shape, {
            showCenterPin: !!(annotation && annotation.Attributes && annotation.Attributes.RadiusSearch === true)
          });
        }
        shape.setOptions({clickable: false});
        shape.set('zIndex', 10);

        self.drawingManager.add(shape,annotation);
        if (annotation == null) {
          var shapeOptions = mapConverters.defaultShapeProperties;
          shape.set("fillColor", shapeOptions.fillColor);
          shape.set("strokeColor", shapeOptions.strokeColor);
          shape.set("fillOpacity", shapeOptions.fillOpacity);
        }
      });
    }
  },

  getAnnotationByCondition: function(annotations, condition) {
    for (var x = 0; x < annotations.length; x++) {
      if (annotations[x].Match.Substring == condition) {
        return annotations[x];
      }
    }
    return null;
  },

  drawParcelOutline: function(polygons) {
    let parcelOutline = new google.maps.Polygon({
      path: polygons,
      geodesic: true,
      strokeColor: '#0077d9',
      strokeOpacity: 1.0,
      strokeWeight: 2,
      fillColor: '#0077d9',
      fillOpacity: 0.35
    });
    this.parcelOutlines.push(parcelOutline);
    parcelOutline.setMap(this.googleMap);
  },

  deleteParcelOutlines: function() {
    this.parcelOutlines.forEach(function(drawing){
      drawing.setMap(null);
    });
    this.parcelOutlines = [];
  },

  restoreOverlaysToMap: function() {
    var ids = [];
    let filter = getState('filter');

    filter.overlays().forEach(function(overlay) {
      overlay.value.forEach(function(value) {
        ids.push(value);
      });
    });

    this.overlayManager.findAndAddShapes(ids);
  },

  getGeoposition: function() {
    var self = this;
    var deferredObject = $.Deferred();
    var myLocation = new MyLocation();

    if(typeof this._geoposition !== 'undefined'){
      deferredObject.resolve(self._geoposition);
    } else {

      function success(geoposition) {
        self._geoposition = {
          lat: geoposition.coords.latitude,
          lng: geoposition.coords.longitude
        };
        deferredObject.resolve(self._geoposition);
      }

      function error(message) {
        deferredObject.resolve();
      }

      myLocation.get({success: success, error: error});
    }

    return deferredObject;
  },

  moveMapToMlsDefaultBounds: function() {
    this._log( 'moving map to the MLS default bounds' );
    return this.moveMapToBoundary(this.mlsDefaultBounds);
  },

  // When skipMapClustersWhenFilterIsBaseline is set (filter-driven search types):
  // - Blank filter is baseline (mls_number until MLS # criteria are added).
  // - Filter equal to originalFilter is baseline (address searches: default MlsId scope only).
  _filterIsBaseline: function(filterStr) {
    var trimmed = (filterStr || '').trim();
    if (trimmed === '') return true;
    var original = (mapSupport.originalFilter || '').trim();
    return original.length > 0 && trimmed.toLowerCase() === original.toLowerCase();
  },

  cancelPendingClusterRequests: function() {
    if (this.mapDataFetcher) {
      this.mapDataFetcher.cancelPendingClusterRequests();
    }
    this._listingDataRequestId = (this._listingDataRequestId || 0) + 1;
    this._stopLoadingMessage();
  },

  _getListingData: function(filter, bounds) {
    this._log( 'getting listing data' );
    var self = this;
    var listingDataPromise = $.Deferred();

    // Increment a counter per listing-data fetch.
    // If we get a response for something other than the current, we will throw it away.
    this._listingDataRequestId = (this._listingDataRequestId || 0) + 1;
    var requestId = this._listingDataRequestId;

    var filterStr = '';
    if (typeof filter === 'string') {
      filterStr = filter;
    } else if (filter && typeof filter.toString === 'function') {
      filterStr = filter.toString();
    }
    if (mapSupport.skipMapClustersWhenFilterIsBaseline && this._filterIsBaseline(filterStr)) {
      var emptyListingData = { Clusters: [], Boundary: null };
      self.listingData = emptyListingData;
      self._setNewVisibleCountFromClustersData();
      listingDataPromise.resolve(emptyListingData);
      return listingDataPromise;
    }

    this._startLoadingMessage();

    var dataFetcherPromise = this.mapDataFetcher.fetch(filter, bounds);

    dataFetcherPromise.done(function(responseData) {
      if (requestId !== self._listingDataRequestId) {
        // Not the current request.  Dont process this response.
        listingDataPromise.reject({ superseded: true });
        return;
      }
      self.listingData = responseData;

      self._setNewVisibleCountFromClustersData();
      if (self.filterIncompatibleWithClusters()) {
        self.setIncompatibleLinkWarning();
      }
      listingDataPromise.resolve(responseData);
    });

    dataFetcherPromise.fail(function(error){
      if (requestId !== self._listingDataRequestId) {
        listingDataPromise.reject(error);
        return;
      }
      if (axios.isCancel(error)) {
        listingDataPromise.reject(error);
        return;
      }
      if (isClustersRequestTimeoutError(error)) {
        const total = getState('listingCountTotal');
        const timeoutMessage = Number(total) >= 100000 ? MAP_CLUSTER_TIMEOUT_MESSAGE_NARROW : MAP_CLUSTER_TIMEOUT_MESSAGE_GENERIC;
        toast.warning(timeoutMessage);
        markerManager.deleteMarkers();
        markerManager.deletePopupMarkers();
        self.listingData = { Clusters: [], Boundary: null };
        listingDataPromise.reject(error);
        return;
      }
      var errorMessage = null;

      // Look for error information.  We are specifically looking
      // for the error message that indicates there are too many
      // coordinates defined in the search.
      if (error && error.response && error.response.data) {
        var responseData = error.response.data;
        if (responseData.message) {
          errorMessage = responseData.message;
        } else if (typeof responseData === 'string') {
          errorMessage = responseData;
        }
      }

      // Check for the specific location coordinates error
      // Matches messages like: "Location search total coordinates must be below 200."
      // or: "Address search total coordinates must be below 200."
      var coordinateErrorRegex = new RegExp('.+ ' + SHAPE_COORDINATE_ERROR_MESSAGE + ' \\d+\\.');
      if (errorMessage && typeof errorMessage === 'string' && coordinateErrorRegex.test(errorMessage)) {
        self.showErrorModal(COMPLEX_SHAPE_USER_ERROR_MESSAGE);
      } else {
        self.showErrorModal();
      }

      listingDataPromise.reject(error);
    });

    listingDataPromise.always(function() {
      if (requestId === self._listingDataRequestId) {
        self._stopLoadingMessage();
      }
    });

    return listingDataPromise;
  },

  getBounds: function() {
    var self = this;
    let promise = new Promise(function(resolve, reject) {

      if (typeof self.googleMap  === 'undefined') {
        reject(new Error('the map is undefined'));
      }

      let bounds = self.googleMap.getBounds();

      // If the map is not yet initialized or center and zoom have not been
      // set then the result is undefined.
      if (bounds !== null && typeof bounds !== 'undefined'){
        resolve( bounds );
      } else {
        // wait for the idle event, or fail after 5 seconds

        google.maps.event.addListenerOnce(self.googleMap, 'idle', () => {
          bounds = self.googleMap.getBounds();
          if(bounds){
            resolve(bounds);
          } else {
            reject(new Error('could not get bounds from map even after idle event'));
          }
        });

        setTimeout(() => {
          reject(new Error('could not get bounds from map after 5 seconds'));
        }, 5000);
      }

    });
    return promise;

  },

  getZoom: function() {
    return this.googleMap.getZoom();
  },

  filterIncompatibleWithClusters: function() {
    let filter = getState('filter');
    return ( filter.toString().indexOf('EmailLink') > -1 || filter.toString().indexOf('SavedSearch') > -1 );
  },

  setIncompatibleLinkWarning: function() {
    var totalCount = getState('listingCountTotal');
    var visibleCount = getState('listingCountVisible');
    this.removeWarningMessage();
    if ( totalCount > 100 && visibleCount === 100 ) {
      var warningMessage = 'This search contains ' + totalCount + ' listings, but only 100 are shown. Zoom in to see more.';
      this.addWarning(warningMessage);
    }
  },

  showErrorModal: function(errorMessage) {
    if(typeof errorMessage == 'undefined'){
      errorMessage = this.translations.error;
    }
    alertModal(errorMessage);
  },

  removeWarningMessage: function() {
    $('.mapWarning').remove();
  },

  addWarning: function(warningMessage) {
    var flash = '<div class="ui-corner-all flash info mapWarning flash-map">';
    flash += '<p>' + warningMessage + '</p>';
    flash += '</div>';
    $('#map').prepend(flash);
  },

  // Returns a promise that resolves the new bounds when successful
  moveMapToListingDataBoundary: function(){

    if( this.listingData.Boundary === null && this.listingData.Clusters.length === 1) {
      var points = mapConverters.sparkBoundaryToPoints(this.listingData.Clusters[0].Centroid);
      // Zoom to nosy neighbor level for single listing detail
      this.googleMap.setZoom(NOSY_NEIGHBOR_ZOOM_LEVEL);
      this.moveMapToPoint(points[0]);
      return this.getBounds();
    } else if( this.listingData.Boundary !== null && typeof this.listingData.Boundary !== 'undefined' ) {
      var boundaryPoints = mapConverters.sparkBoundaryToPoints(this.listingData.Boundary);
      var newBounds = mapConverters.getGoogleBoundsFromPoints(boundaryPoints);
      return this.moveMapToBoundary(newBounds);
    } else {
      return Promise.reject(new Error('cannot move the map to the listing data because the listing data is missing'));
    }
  },

  // Reposition map from current listing data:
  // - fit to listing boundary when listings exist
  // - fall back to MLS defaults when there are no listings or boundary data is missing
  // Always persists resulting bounds to state.
  fitMapToCurrentListingResults: function(overrides) {
    overrides ||= {};
    const hasListings = this.hasListingResults();
    const movePromise = hasListings
      ? this.moveMapToListingDataBoundary().catch(() => this.moveMapToMlsDefaultBounds())
      : this.moveMapToMlsDefaultBounds();

    return movePromise.then((bounds) => {
      if (overrides.suppressBoundsRefetch) {
        this._suppressBoundsChangedRefetch = true;
        setState('mapBounds', bounds, { triggerEvent: false });
        window.setTimeout(() => {
          this._suppressBoundsChangedRefetch = false;
        }, SUPPRESS_BOUNDS_CHANGED_REFETCH_MS);
      } else {
        setState('mapBounds', bounds);
      }
      return bounds;
    });
  },

  hasListingResults: function() {
    if (!this.listingData || !Array.isArray(this.listingData.Clusters)) {
      return false;
    }

    return this.listingData.Clusters.some((cluster) => {
      if ((cluster.Count || 0) > 0) {
        return true;
      }

      return Array.isArray(cluster.Listings) && cluster.Listings.length > 0;
    });
  },

  // Fetch listing data/markers, then reposition viewport using current results.
  updateMapAndFitToCurrentListingResults: function(overrides) {
    const deferredObject = $.Deferred();

    this.updateMap(overrides)
      .done(() => {
        this.fitMapToCurrentListingResults(overrides)
          .then((bounds) => deferredObject.resolve(bounds))
          .catch((error) => deferredObject.reject(error));
      })
      .fail((error) => deferredObject.reject(error));

    return deferredObject;
  },

  updateMap: function(overrides){
    var self = this;
    var deferredObject = $.Deferred();

    overrides ||= {};

    const filter = typeof overrides.filter !== 'undefined' ? overrides.filter : getState('filter').toString();
    const bounds = typeof overrides.bounds !== 'undefined' ? overrides.bounds : getState('mapBounds');

    self._getListingData(filter, bounds).done(function() {
      self.drawMarkers();
      deferredObject.resolve();
    }).fail(function(error) {
      deferredObject.reject(error);
    });
    return deferredObject;
  },

  _setNewVisibleCountFromClustersData: function() {
    if(!mapSupport.isListDriven !== false){
      setState('listingCountVisible', this.mappedListingCount());
    }
  },

  mappedListingCount: function() {
    // The total count is getting stored as a property on the listing data object.
    // When the map data is reloaded, the whole listingData object will be replaced,
    // so the TotalCount attribute should never be out of date.
    if(typeof this.listingData.TotalCount === 'undefined'){
      this.listingData.TotalCount = this.listingData.Clusters.reduce((accumulator, cluster) => {
        return accumulator + (cluster.Count || 0);
      }, 0);
    }
    return this.listingData.TotalCount;
  },

  drawMarkers: function() {
    markerManager.deleteMarkers();

    if ($('.shape-edit-in-progress').length > 0) return;

    var self = this;
    this.listingData.Clusters.forEach(function(cluster){
      if (cluster.Listings === null){
        markerManager.addClusterMarker(cluster);
      } else {
        self.plotListings.call(self, cluster.Listings);
      }
    });
  },

  moveMapToBoundary: function(googleBoundsObject) {
    let self = this;

    if (this.debugBounds && googleBoundsObject){
      let shape = new google.maps.Rectangle({bounds: googleBoundsObject});
      shape.setMap(this.googleMap);
    }

    // There are cases where we want to know when the map is done being moved.
    // This returns a promise that resolves the new bounds.
    let promise = new Promise(function(resolve, reject) {

      if(typeof googleBoundsObject === 'undefined'){
        self.getBounds().then((bounds) => resolve(bounds) );
      } else {
        // This sets up a one time event listener so that when the map next becomes
        // idle, we can get the bounds and return them.
        google.maps.event.addListenerOnce(self.googleMap, 'idle', () => {
          self.getBounds().then((bounds) => {
            resolve(bounds)
          });
        });

        self.googleMap.fitBounds(googleBoundsObject, 0); // second argument is for padding
      }
    });

    return promise;
  },

  moveMapToPoint: function(googleLatLngObject) {
    let self = this;

    return new Promise(function(resolve, reject) {
      google.maps.event.addListenerOnce(self.googleMap, 'idle', () => {
        self.getBounds().then((bounds) => {
          resolve(bounds);
        });
      });

      self.googleMap.setCenter(googleLatLngObject);
    });
  },

  plotListings: function(listingsArray) {
    listingsArray.forEach(this.plotListing.bind(this));
  },

  plotListing: function(listing) {
    var self = this;

    if( this.mappedListingCount() < this.pricePinCutoff && listing.StandardFields.CurrentPrice !== null &&
        typeof listing.StandardFields.CurrentPrice !== 'undefined' ) {
      markerManager.addPriceMarker(listing, this.currencySymbol);
    } else {
      var opts = {
        listingId: listing.Id,
        lat: listing.StandardFields.Latitude,
        lng: listing.StandardFields.Longitude,
        listingStatus: listing.StandardFields.StandardStatus
      }
      markerManager.addListingMarker(opts);
    }
  },

  bindMapEvents: function() {
    this.bindBoundsChanges();
    this.bindZoomChanges();
    this.googleMap.addListener('dragstart', this.deleteTemporaryMarkersAndStuff.bind(this) );
  },

  bindBoundsChanges: function() {
    if (this._debouncedBoundsChangedHandler) {
      this._debouncedBoundsChangedHandler.cancel();
    }
    this._debouncedBoundsChangedHandler = debounce(this.boundsChangedHandler.bind(this), 500);
    this.boundsListener = this.googleMap.addListener('bounds_changed', this._debouncedBoundsChangedHandler);
  },

  bindZoomChanges: function() {
    this.zoomListener = this.googleMap.addListener('zoom_changed', this.handleZoomChange.bind(this));
  },

  deleteTemporaryMarkersAndStuff: function() {
    this.deleteParcelOutlines();
    mapConverters.closeGoogleInfoWindows()
    markerManager.deletePopupMarkers();
  },

  boundsChangedHandler: function() {
    this._log('map bounds change');
    if (this._suppressBoundsChangedRefetch) {
      return;
    }
    if(!this.drawingManager.drawingInProgress()){
      // it's safe to use googleMap.getBounds here because we are in the
      // bounds_changed even handler that was triggered by the map
      setState('mapBounds', this.googleMap.getBounds() );
    }
  },

  handleZoomChange: function() {
    // Shapes should be hidden at nosy neighbor zoom level or higher
    const shapesShouldBeVisible = (this.googleMap.getZoom() < NOSY_NEIGHBOR_ZOOM_LEVEL);

    this.drawingManager.toggleVisibility(shapesShouldBeVisible);
    GlobalOverlayManager.toggleFilteredOverlayShapesVisibility(shapesShouldBeVisible);
    GlobalOverlayManager.toggleUnfilteredOverlayShapesVisibility(shapesShouldBeVisible);
  },

  //
  // When the map is frozen:
  //
  // - stateChange:filter events will be ignored
  // - stateChange:mapBounds events will be ignored
  // - the bounds_change listener on the google map will be temporarily removed,
  //   so moving the map will not trigger mapBounds state changes
  //
  // This function is useful if you want to move the map programatically
  // without it triggering a data refresh, or if you need to update both the
  // filter and mapBounds in the state, but don't want to trigger two refreshes.
  //
  // If a function is passed as an argument, the function will be run while the
  // map is frozen and it will be unfrozen automatically afterwards.
  //
  // If no argument is given, then `unfreeze` needs to be called manually.
  //
  freeze: function(doWhileFrozen) {
    const previouslyWasFrozen = this._frozen;
    this._log( 'freezing map' );
    this._frozen = true;

    if (this._debouncedBoundsChangedHandler) {
      this._debouncedBoundsChangedHandler.cancel();
    }
    google.maps.event.removeListener(this.boundsListener);

    if(typeof doWhileFrozen === 'function'){
      doWhileFrozen();

      // If the map was previously frozen before freeze was called with a function
      // then we probably shouldn't unfreeze it after the callback runs
      if(!previouslyWasFrozen){
        this.unfreeze();
      }
    }
  },

  unfreeze: function() {
    this._log( 'unfreezing map' );
    this._frozen = false;
    this.bindBoundsChanges();

    if (this._suppressBoundsChangedRefetch || (this.drawingManager && this.drawingManager.drawingInProgress())) {
      this._pendingBoundsClusterRefresh = false;
      return;
    }

    const bounds = this.googleMap.getBounds();
    const currentBounds = getState('mapBounds');
    const boundsChanged = bounds && (!currentBounds || currentBounds.toString() !== bounds.toString());

    if (boundsChanged) {
      setState('mapBounds', bounds);
      this._pendingBoundsClusterRefresh = false;
      return;
    }

    if (this._pendingBoundsClusterRefresh) {
      this._pendingBoundsClusterRefresh = false;
      this.updateMap();
    }
  },

  bindMapControls: function() {
    //$('#drawOnMapBtn:not(.bound)').addClass('bound').on('click', this.drawOnMapClickHandler.bind(this));
    $('#discardDrawingsBtn:not(.bound)').addClass('bound').on('click', this.startDrawing.bind(this));
    $('#cancelDrawOnMapBtn:not(.bound)').addClass('bound').on('click', this.cancelDrawingHandler.bind(this));

    $('#shapeMenuDeleteShape:not(.bound)').addClass('bound').on('click', this.confirmDeleteDrawing.bind(this));
    $('#deleteDrawingBtn:not(.bound)').addClass('bound').on('click', this.deleteDrawing.bind(this));
    $('#cancelDeleteDrawingBtn:not(.bound)').addClass('bound').on('click', this.cancelDeleteDrawing.bind(this));
    // This will be called when a the popover is finished constructing/showing itself.
    $('.shapesPopover').on('shown.bs.popover', this.shapesPopoverVisibleHandler.bind(this));
  },

  unBindMapControls: function(){
    $('#discardDrawingsBtn').removeClass('bound').off('click', this.startDrawing.bind(this));
    $('#cancelDrawOnMapBtn').removeClass('bound').off('click', this.cancelDrawingHandler.bind(this));

    $('#shapeMenuDeleteShape').removeClass('bound').off('click', this.confirmDeleteDrawing.bind(this));
    $('#deleteDrawingBtn').removeClass('bound').off('click', this.deleteDrawing.bind(this));
    $('#cancelDeleteDrawingBtn').removeClass('bound').off('click', this.cancelDeleteDrawing.bind(this));
    $('.shapesPopover').off('shown.bs.popover', this.shapesPopoverVisibleHandler.bind(this));
  },

  doDrawing: function(e) {
    switch(e.target.id)
    {
      case "shapeChoiceCircleBtn":
        this.drawingShape = "circle";
        break;
      case "shapeChoicePolygonBtn":
        this.drawingShape = "polygon";
        break;
      case "shapeChoiceRectangleBtn":
        this.drawingShape = "rectangle";
        break;
      default:
        return;
    }
    this.startDrawing();
  },

  shapesPopoverVisibleHandler: function(e) {
    // When the popover is visible we can attach the close button handler.
    // Before this, the close button doesnt exist.
    // Using the 'bound' class to make sure we don't add the click handler multiple times.
    $('.map-shapes-popover').find('.map-shape-choice-control:not(.bound)').addClass('bound').on('click', this.doDrawing.bind(this));

    // Make shapes visible when the popover is first opened
    this.drawingManager.toggleVisibility(true);
    GlobalOverlayManager.toggleFilteredOverlayShapesVisibility(true);
    GlobalOverlayManager.toggleUnfilteredOverlayShapesVisibility(true);
  },

  /**
   * Geolocate → pan → zoom → wait for idle → persist viewport to mapBounds.
   * @returns JQuery Deferred (always resolves).
   */
  whenMyLocationPannedThen: function(evt) {
    const self = this;
    evt && evt.preventDefault();
    self._startLoadingMessage();

    const job = new Promise(function(resolveOuter) {
      self.getGeoposition().done(function(geoposition) {
        if (typeof geoposition === 'undefined') {
          resolveOuter();
          return;
        }

        const point = new google.maps.LatLng(geoposition.lat, geoposition.lng);
        markerManager.addMyLocationMarker(point);

        Promise.resolve(self.moveMapToPoint(point))
          .then(() => {
            try {
              self.googleMap.setZoom(15);
            } catch (_e) {
              return Promise.resolve();
            }
            return new Promise(function(innerResolve) {
              google.maps.event.addListenerOnce(self.googleMap, 'idle', innerResolve);
            });
          })
          .then(() => self.getBounds())
          .then((bounds) => {
            if (bounds) {
              setState('mapBounds', bounds);
            }
          })
          .catch(() => { /* tolerate bad bounds temporarily */ })
          .then(() => resolveOuter());
      });
    });

    const out = $.when(job);
    out.always(function() {
      self._stopLoadingMessage();
    });
    return out;
  },

  myLocationClickEventHandler: function(e) {
    return this.whenMyLocationPannedThen(e);
  },

  getLocationAndAddMarker: function() {
    var self = this;
    this.getGeoposition().done(function(geoposition) {
      if(typeof geoposition !== 'undefined'){
        markerManager.addMyLocationMarker(geoposition);
      }
    });
  },

  drawOnMapClickHandler: function(e) {
    e.preventDefault();
    this.startDrawing();
  },

  startDrawing: function() {
    markerManager.deleteMarkers();
    this.turnOnDrawingUI();

    // While drawing, ignore clicks on existing shapes.
    this.drawingManager.setExternalDrawingInProgress(true);
    this.drawingManager.disableDrawingsClickability();
    this.shapeEditor.startDrawing(this.drawingShape);
  },

  turnOnDrawingUI: function() {
    $('#drawOnMapBtn').hide();
    $('#cancelDrawOnMapBtn').show();

    this.showDrawingModeHeader();
  },

  turnOffDrawingUI: function() {
    $('#cancelDrawOnMapBtn').hide();
    $('#drawOnMapBtn').show();
    this.hideDrawingModeHeader();
  },

  cancelDrawingHandler: function(e) {
    e.preventDefault();

    let filter = getState('filter');

    const countParams = {
      _filter: filter.toString()
    };

    axios.get(mapSupport.listingCountRetrieverUrl, {params: countParams}).then((response) => {
      setState('listingCountTotal', response.data);
    });

    this.shapeEditor.cancelDrawing();
    // Ensure shield is lifted even if callbacks change.
    this.drawingManager.setExternalDrawingInProgress(false);
    this.drawingManager.restoreDrawingsClickability();
    this.turnOffDrawingUI();
  },

  drawingComplete: function(e) {
    const shape = e.detail?.shape ?? e.detail;
    const isRadiusSearch = e.detail?.radiusSearch === true;
    if (!shape) return;

    const filter = getState('filter');

    // Try to handle via filter panel first
    if (this._canUseFilterPanel()) {
      const handled = this._handleShapeViaFilterPanel(shape, isRadiusSearch);
      if (handled) {
        this.turnOffDrawingUI();
        return;
      }
    }

    // Fallback to direct filter addition
    const shapeResults = this._addShapeToFilter(shape, filter);
    const newBounds = this._calculateShapeBounds(shape);
    
    this.updateFilterPanelSrc(filter);

    this.turnOffDrawingUI();
    this.drawingCompleteCallback(filter, newBounds, shapeResults ? shapeResults.annotation : null, { radiusSearch: isRadiusSearch });
  },

  drawingError: function() {
    var message = "<h4>" + this.translations.invalid_shape + "</h4>";
    message += this.translations.invalid_shape_message;
    this.showErrorModal(message);
  },

  drawnShapeEditComplete: function(e) {
    // Reminder:  This only happens when you hit the 'Done Editing' button.  It does not happen when you drag a shape point and release it.

    let filter = getState('filter');

    // If something goes wrong, fallback to the old condition
    let newCondition = e.detail.oldShapeDef;
    let newFunctionParams = [];

    // Get info needed from the shape to update the filter expression
    if (e.detail.newShape instanceof google.maps.Polygon) {

      const polygonPointsString = mapConverters.getStringFromPoints(e.detail.newShape.getPath());
      newCondition = "polygon('" + polygonPointsString + "')";
      newFunctionParams = [polygonPointsString];

    } else if (e.detail.newShape instanceof google.maps.Circle) {

      let lat = e.detail.newShape.getCenter().lat();
      let lng = e.detail.newShape.getCenter().lng();
      let radiusInMiles = e.detail.newShape.getRadius() / 1609.34;
      newCondition = "radius('" + parseFloat(lat.toFixed(6)) + " " + parseFloat(lng.toFixed(6)) + "'," + parseFloat(radiusInMiles.toFixed(2)) + ")";
      newFunctionParams =  [ parseFloat(lat.toFixed(6)) + " " + parseFloat(lng.toFixed(6)), parseFloat(radiusInMiles.toFixed(2)) ];

    } else if (e.detail.newShape instanceof google.maps.Rectangle) {

      let bounds = e.detail.newShape.getBounds();
      const rectanglePointsString = mapConverters.getStringFromPoints([bounds.getNorthEast(), bounds.getSouthWest()]);
      newCondition = "rectangle('" + parseFloat(bounds.getNorthEast().lat().toFixed(6)) + " " + parseFloat(bounds.getNorthEast().lng().toFixed(6)) + "," + parseFloat(bounds.getSouthWest().lat().toFixed(6)) + " " + parseFloat(bounds.getSouthWest().lng().toFixed(6)) +  "')";
      newFunctionParams = [rectanglePointsString]

    }

    // Make sure something changed
    if (newCondition !== e.detail.oldShapeDef) {
      // Update the annotations

      // Make a copy of annotations so the state's actual annotations value
      // doesn't change until we call setState.
      let annotations = JSON.parse(JSON.stringify(getState('annotations')));

      annotations.forEach((a, index) => {
        if (a.Match.Substring === e.detail.oldShapeDef) {
          annotations[index].Match.Substring = newCondition;
        }
      });

      setState('annotations',annotations)
      sessionStorage.setItem('annotations', JSON.stringify(annotations))

      // Now update the filter
      filter.replaceShape(e.detail.oldShapeDef, newCondition, newFunctionParams)
      // And update the shape so the condition matches what it should match.
      e.detail.newShape.set('condition',newCondition)
    }

    let bounds = this.calculateBoundsFromAllShapes()

    this.drawingCompleteCallback(filter, bounds);
  },

  calculateBoundsFromAllShapes() {
    var shapeBounds = this.drawingManager.getBoundsForMultipleShapes();
    let overlayBounds = GlobalOverlayManager.getTotalMapOverlayShapeBounds();
    if (shapeBounds === null) {
      shapeBounds = overlayBounds
    } else if (overlayBounds !== null) {
      shapeBounds.union(overlayBounds)
    }
    return shapeBounds;
  },

  confirmDeleteDrawing: function(event) {
    event.preventDefault();
    if (this.drawingManager.selectedShape == null) {
      return;
    }
    $('#drawnShapeManagementMenu').css({ display: 'none'});
    $("#confirmDeleteDrawingModal").modal('show');
  },

  cancelDeleteDrawing: function(event) {
    event.preventDefault();
    this.drawingManager.selectedShape = null;
    this.drawingManager.resetAllShapesOpacity();
  },

  deleteDrawing: function(event) {
    event.preventDefault();
    // The drawing manager knows what shape we want to delete.
    if (this.drawingManager.selectedShape == null) {
      return;
    }

    let filter = getState('filter');
    let shapeCondition = this.drawingManager.selectedShape.get('condition');

    // Remove drawn shape annotation using helper
    removeDrawnShapeAnnotation(shapeCondition);

    // Remove the shape from the filter
    filter.removeShapeByCondition(shapeCondition);
    // Remove any labels associated with the shape
    markerManager.removeShapeMarker(this.drawingManager.selectedShape);
    // Remove the shape from the map
    this.drawingManager.deleteDrawing(this.drawingManager.selectedShape);
    // Get the bounds for remaining shapes
    let newBounds = this.calculateBoundsFromAllShapes();

    if (newBounds) {
      // One or more shapes still remain.  Pretend we just drew a shape
      // and let the callback update the map and the counts.
      this.drawingCompleteCallback(filter, newBounds);
    } else {
      // No shapes left.  Need to use the default bounds and update the
      // map and the count here.
      setState('filter',filter);
      this.updateFilterPanelSrc(filter);
      filter = filter.toString();

      this.moveMapToMlsDefaultBounds();
      this.getBounds().then((bounds) => {
        this._getListingData(filter, bounds).done(() => {
          // Update the 'Viewing x of x' count
          axios.get(mapSupport.listingCountRetrieverUrl, {params: {_filter: filter}}).then((response) => {
            setState('listingCountTotal', response.data);
          });

          if (reasonableNumberShown(this.listingData, bounds)) {
            this.drawMarkers();
            setState('mapBounds', bounds);
          } else {
            this._setUpMapAroundListingData(filter);
          }
        });
      });
    }


  },

  _startLoadingMessage: function() {
    if(typeof this.loadingMessage !== 'undefined' && typeof this.loadingMessage.start === 'function'){
      this.loadingMessage.start();
    }
  },

  _stopLoadingMessage: function() {
    if(typeof this.loadingMessage !== 'undefined' && typeof this.loadingMessage.stop === 'function'){
      this.loadingMessage.stop();
    }
  },

  _log: function(message) {
    if(this.debug){
      console.log('[FlexmlsMap] ' + message);
    }
  },

  resetShapesMenu() {
    this.drawingManager.resetShapesMenu()
  },

  highlightDrawnShape(shapeDef) {
    this.drawingManager.highlightShape(shapeDef)
  },

  unhighlightDrawnShape(shapeDef) {
    this.drawingManager.unhighlightShape(shapeDef)
  },

  getDrawnShapesBounds() {
    return this.drawingManager.getBoundsForMultipleShapes()
  },

  deleteAllDrawings() {
    this.drawingManager.deleteAll()
  },

  displayOverlayManager(details) {
    this.drawingManager.displayOverlayManager(details, mapModals.addShapeToOverlayModal)
  },

  _addPolygonToFilterPanel(polygon, pointsArray) {
    // We need to create an option that the drawingsSelect can use.  Processing the select2 change will properly create
    // the filter based on the form content.
    // We also need to create an annotation for the polygon.
    const pointsString = mapConverters.getStringFromPoints(pointsArray);
    const polygonElement = $(`<option value="polygon('${pointsString}')" selected="selected" data-color="#${polygon.get("fillColor")}">Polygon</option>`);

    // Create drawn shape annotation using helper
    createDrawnShapeAnnotation(
      polygonElement.val(),
      'Polygon',
      polygon.get("fillColor")
    );

    this.adjustDrawnShapesOperatorForNewShapeIntersection();

    // Add the polygon to the filter panel
    $('#drawingsSelect').append(polygonElement);
    $('#drawingsSelect').trigger('change');
    // The trigger will take care of adding the polygon to the filter
  },

  _addCircleToFilterPanel(circle, radiusInMiles, isRadiusSearch = false) {
    // We need to create an option that the drawingsSelect can use.  Processing the select2 change will properly create
    // the filter based on the form content.
    // We also need to create an annotation for the circle.
    const lat = circle.getCenter().lat();
    const lng = circle.getCenter().lng();
    const circleElement = $(`<option value="radius('${parseFloat(lat.toFixed(6))} ${parseFloat(lng.toFixed(6))}',${parseFloat(radiusInMiles.toFixed(2))})" selected="selected" data-color="#${circle.get("fillColor")}">Circle</option>`);

    // Create the annotation for the circle
    // Create drawn shape annotation using helper
    createDrawnShapeAnnotation(
      circleElement.val(),
      'Circle',
      circle.get("fillColor"),
      { radiusSearch: isRadiusSearch }
    );

    this.adjustDrawnShapesOperatorForNewShapeIntersection();

    // Add the circle to the filter panel
    $('#drawingsSelect').append(circleElement);
    $('#drawingsSelect').trigger('change');
    // The trigger will take care of adding the circle to the filter
  },

  _addRectangleToFilterPanel(rectangle) {
    // We need to create an option that the drawingsSelect can use.  Processing the select2 change will properly create
    // the filter based on the form content.
    // We also need to create an annotation for the rectangle.
    const bounds = rectangle.getBounds();
    const rectangleElement = $(`<option value="rectangle('${parseFloat(bounds.getNorthEast().lat().toFixed(6))} ${parseFloat(bounds.getNorthEast().lng().toFixed(6))},${parseFloat(bounds.getSouthWest().lat().toFixed(6))} ${parseFloat(bounds.getSouthWest().lng().toFixed(6))}')" selected="selected" data-color="#${rectangle.get("fillColor")}">Rectangle</option>`);

    // Create drawn shape annotation using helper
    createDrawnShapeAnnotation(
      rectangleElement.val(),
      'Rectangle',
      rectangle.get("fillColor")
    );

    this.adjustDrawnShapesOperatorForNewShapeIntersection();

    // Add the rectangle to the filter panel
    $('#drawingsSelect').append(rectangleElement);
    $('#drawingsSelect').trigger('change');
    // The trigger will take care of adding the rectangle to the filter
  },

  // Helper methods for refactored drawingComplete
  _getDrawnShapesOperator() {
    return $('#search_fields_location_operator').length ? $('#search_fields_location_operator').val() : null;
  },

  _canUseFilterPanel() {
    return $('#drawingsSelect').length > 0;
  },

  _handleShapeViaFilterPanel(shape, isRadiusSearch = false) {
    if (shape instanceof google.maps.Polygon) {
      this._addPolygonToFilterPanel(shape, shape.getPath());
      return true;
    } else if (shape instanceof google.maps.Circle) {
      const radius = this._validateCircleRadius(shape);
      const radiusInMiles = radius / 1609.34;
      this._addCircleToFilterPanel(shape, radiusInMiles, isRadiusSearch);
      return true;
    } else if (shape instanceof google.maps.Rectangle) {
      this._addRectangleToFilterPanel(shape);
      return true;
    }
    return false;
  },

  _validateCircleRadius(circle) {
    let radius = circle.getRadius();
    // Minimum radius = 0.01 miles (16.0934 meters).
    if (radius < 16.0934) {
      radius = 16.0934;
      circle.setRadius(radius);
    }
    return radius;
  },

  _addShapeToFilter(shape, filter) {
    if (shape instanceof google.maps.Polygon) {
      const pointsArray = shape.getPath();
      const results = filter.addPolygon(pointsArray, "Polygon", shape.get("fillColor"));
      shape.set('condition', results.expression.condition);
      return results;
    } else if (shape instanceof google.maps.Circle) {
      const radius = this._validateCircleRadius(shape);
      const radiusInMiles = radius / 1609.34;
      const results = filter.addRadius(shape.getCenter().lat(), shape.getCenter().lng(), radiusInMiles, "Circle", shape.get("fillColor"));
      shape.set('condition', results.expression.condition);
      return results;
    } else if (shape instanceof google.maps.Rectangle) {
      const results = filter.addRectangle(shape.getBounds(), "Rectangle", shape.get("fillColor"));
      shape.set('condition', results.expression.condition);
      return results;
    }
    return null;
  },

  _getBoundsFromFilterShapes: function() {
    try {
      const filter = getState('filter');
      if (!filter || !filter.shapes || filter.shapes().length === 0) {
        return null;
      }
      let bounds = null;
      filter.shapes().forEach((element) => {
        const shape = mapConverters.getGoogleShapeFromFilterShape(element);
        let shapeBounds = null;
        if (shape instanceof google.maps.Polygon) {
          shapeBounds = mapConverters.getGoogleBoundsFromPoints(shape.getPath());
        } else if (shape instanceof google.maps.Circle || shape instanceof google.maps.Rectangle) {
          shapeBounds = shape.getBounds();
        }
        if (shapeBounds) {
          if (bounds === null) {
            bounds = shapeBounds;
          } else {
            bounds.union(shapeBounds);
          }
        }
      });
      return bounds;
    } catch (e) {
      return null;
    }
  },

  _calculateShapeBounds(shape) {
    let bounds;
    if (this.drawingManager.drawings.length > 1) {
      bounds = this.drawingManager.getBoundsForMultipleShapes();
    } else if (shape instanceof google.maps.Polygon) {
      bounds = mapConverters.getGoogleBoundsFromPoints(shape.getPath());
    } else if (shape instanceof google.maps.Circle) {
      bounds = shape.getBounds();
    } else if (shape instanceof google.maps.Rectangle) {
      bounds = shape.getBounds();
    }

    // Combine with overlay bounds
    const overlayBounds = GlobalOverlayManager.getTotalMapOverlayShapeBounds();
    if (bounds === null) {
      bounds = overlayBounds;
    } else if (overlayBounds !== null) {
      bounds.union(overlayBounds);
    }
    return bounds;
  },

  updateFilterPanelSrc(filter) {
    // Only update filter panel source if the filter panel hasn't been loaded yet.
    if ($('#filterPanel').length > 0) {
      // Filter panel has already been loaded.
      return;
    }

    const leftToolFrame = document.getElementById('leftTool');
    if (leftToolFrame) {

      const newFilterString = filter.toString();

      // For turbo-frames, we need to update the src and force a reload
      const url = new URL(leftToolFrame.src);
      if (newFilterString) {
        url.searchParams.set('_filter', newFilterString);
      } else {
        url.searchParams.delete('_filter');
      }

      // Force the turbo-frame to reload with the new filter
      leftToolFrame.src = url.toString();
    }
  },

  adjustDrawnShapesOperatorForNewShapeIntersection() {
    // If a this is the first new drawn shape we need to look at overlay shape operators.
    // If any are set to Intersection, we need to use an intersection for drawn shapes.

    // No need to adjust the operator if there are existing drawn shapes.
    if ($("#drawingsSelect option").length > 0) return;

    if ($('.has-shapes-operator[data-active="and"]').length > 0){
      // We are adding a new shape when other shapes are defined as intersection.
      // Our drawn shapes operator should be unlocked and set to intersection.
      $('#drawnShapesOperator').removeClass('locked');
      $('#drawnShapesOperator').attr('data-active', 'and');
      $('#drawnShapesOperator').val('and');
      $('#drawnShapesOperator button.c-carousel-button div').text('Intersection');
      $('#search_fields_location_operator').val('and');
    }
  }
};
